import React, { useState } from "react";
import {
  Modal,
  message,
  DatePicker,
  Select,
  InputNumber,
  Form,
  Input,
} from "antd";
import styled from "styled-components";
import { useMutation } from "@tanstack/react-query";
import {
  User,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  CreditCard,
  PawPrint,
  MessageSquare,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import listingModel from "../../lib/firebase/listingModel";
import { trackEvent } from "../../helpers/mixpanel";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Option } = Select;

/**
 * InquiryModal - The "Velvet Rope" Lead Capture
 *
 * Instead of a generic contact form, we ask qualifying questions
 * to filter leads and provide landlords with high-quality prospects.
 */
const InquiryModal = ({ listing, isOpen, onClose }) => {
  const [form] = Form.useForm();
  const [isQualified, setIsQualified] = useState(null);
  const [showWarning, setShowWarning] = useState(false);

  // Submit inquiry mutation
  const submitInquiry = useMutation({
    mutationFn: (data) => listingModel.createInquiry(data),
    onSuccess: () => {
      trackEvent("inquiry_submitted", {
        listing_id: listing.id,
        is_qualified: isQualified,
        address: listing.address,
      });

      message.success(
        "Your inquiry has been sent! The landlord will contact you soon."
      );
      form.resetFields();
      onClose();
    },
    onError: (error) => {
      console.error("Error submitting inquiry:", error);
      message.error("Failed to submit inquiry. Please try again.");
    },
  });

  // Check qualification based on form values
  const checkQualification = (changedValues, allValues) => {
    // For rent-to-own without credit check, only need income
    const isRentToOwnNoCredit =
      listing.listingType === "rent_to_own" &&
      listing.rentToOwn &&
      !listing.rentToOwn.creditCheckRequired;

    if (isRentToOwnNoCredit) {
      if (!allValues.monthlyIncome) {
        return;
      }
    } else {
      // Regular rental or rent-to-own with credit check - need both
      if (!allValues.creditScore || !allValues.monthlyIncome) {
        return;
      }
    }

    let qualified = true;
    let warningMessage = "";

    // Check credit score (skip for rent-to-own without credit check)
    if (!isRentToOwnNoCredit && listing.rules?.minCreditScore) {
      const creditScoreMap = {
        excellent_720: 720,
        good_650: 650,
        fair_600: 600,
        unknown: 0,
      };

      const userScore = creditScoreMap[allValues.creditScore] || 0;

      if (userScore < listing.rules.minCreditScore) {
        qualified = false;
        warningMessage = `This property requires a credit score of ${listing.rules.minCreditScore}+. `;
      }
    }

    // Check income
    if (listing.rules?.minIncomeMultiplier) {
      let monthlyPayment;
      if (listing.listingType === "rent_to_own" && listing.rentToOwn) {
        monthlyPayment = listing.rentToOwn.monthlyPayment || listing.rentAmount;
      } else {
        monthlyPayment = listing.rentAmount;
      }

      if (monthlyPayment) {
        const requiredIncome =
          monthlyPayment * listing.rules.minIncomeMultiplier;

        if (allValues.monthlyIncome < requiredIncome) {
          qualified = false;
          const paymentType =
            listing.listingType === "rent_to_own" ? "monthly payment" : "rent";
          warningMessage += `This property requires a monthly income of $${requiredIncome.toLocaleString()}+ (${
            listing.rules.minIncomeMultiplier
          }x ${paymentType}). `;
        }
      }
    }

    // Check pets
    if (listing.rules?.petsAllowed === false && allValues.hasPets === true) {
      qualified = false;
      warningMessage += "This property does not allow pets. ";
    }

    setIsQualified(qualified);
    setShowWarning(!qualified && warningMessage.length > 0);
  };

  const handleSubmit = async (values) => {
    const inquiryData = {
      listingId: listing.id,
      landlordId: listing.landlordId,
      name: values.name,
      email: values.email,
      phone: values.phone,
      moveInDate: values.moveInDate?.toDate?.() || values.moveInDate,
      hasPets: values.hasPets,
      creditScore: values.creditScore,
      monthlyIncome: values.monthlyIncome,
      message: values.message || "",
      isQualified,
      source: "vacancy_page",
      referrer: document.referrer || "direct",
      // Additional screening questions (only include if asked)
      smoking: listing.rules?.askSmoking ? values.smoking : undefined,
      numberOfOccupants: listing.rules?.askOccupants
        ? values.numberOfOccupants
        : undefined,
      hasGuarantor: listing.rules?.askGuarantor
        ? values.hasGuarantor
        : undefined,
      hasLawsuit: listing.rules?.askLawsuit ? values.hasLawsuit : undefined,
      hasFelony: listing.rules?.askFelony ? values.hasFelony : undefined,
    };

    submitInquiry.mutate(inquiryData);
  };

  const formatRent = (amount) => {
    return `$${amount?.toLocaleString()}`;
  };

  return (
    <StyledModal
      title={null}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
    >
      <ModalHeader>
        <ModalTitle>Request a Tour</ModalTitle>
        <ModalSubtitle>
          {listing.address} •{" "}
          {listing.listingType === "rent_to_own" && listing.rentToOwn
            ? formatRent(listing.rentToOwn.monthlyPayment || listing.rentAmount)
            : formatRent(listing.rentAmount)}
          /mo
        </ModalSubtitle>
      </ModalHeader>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={checkQualification}
      >
        {/* Basic Contact Info */}
        <SectionTitle>
          <User size={18} />
          Your Information
        </SectionTitle>

        <Form.Item
          name="name"
          rules={[{ required: true, message: "Please enter your name" }]}
        >
          <StyledInput
            prefix={<User size={16} />}
            placeholder="Full Name"
            size="large"
          />
        </Form.Item>

        <FormRow>
          <Form.Item
            name="email"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
            style={{ flex: 1 }}
          >
            <StyledInput
              prefix={<Mail size={16} />}
              placeholder="Email"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="phone"
            rules={[{ required: true, message: "Please enter your phone" }]}
            style={{ flex: 1 }}
          >
            <StyledInput
              prefix={<Phone size={16} />}
              placeholder="Phone Number"
              size="large"
            />
          </Form.Item>
        </FormRow>

        {/* Qualifying Questions */}
        <SectionTitle>
          <CheckCircle size={18} />
          Qualification Questions
        </SectionTitle>

        <QuestionCard>
          <QuestionLabel>
            <Calendar size={18} />
            When do you need to move?
          </QuestionLabel>
          <Form.Item
            name="moveInDate"
            rules={[
              { required: true, message: "Please select a move-in date" },
            ]}
          >
            <StyledDatePicker
              size="large"
              placeholder="Select move-in date"
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
              style={{ width: "100%" }}
            />
          </Form.Item>
        </QuestionCard>

        <QuestionCard>
          <QuestionLabel>
            <PawPrint size={18} />
            Do you have pets?
          </QuestionLabel>
          <Form.Item
            name="hasPets"
            rules={[{ required: true, message: "Please select an option" }]}
          >
            <ToggleButtons>
              <ToggleButton
                type="button"
                $active={form.getFieldValue("hasPets") === false}
                onClick={() => form.setFieldValue("hasPets", false)}
              >
                No Pets
              </ToggleButton>
              <ToggleButton
                type="button"
                $active={form.getFieldValue("hasPets") === true}
                onClick={() => form.setFieldValue("hasPets", true)}
              >
                Yes, I have pets
              </ToggleButton>
            </ToggleButtons>
          </Form.Item>
          {listing.petsAllowed && listing.petDeposit && (
            <PetNote>Pet deposit: {formatRent(listing.petDeposit)}</PetNote>
          )}
        </QuestionCard>

        {/* Only show credit score if required OR if it's a regular rental */}
        {(listing.listingType !== "rent_to_own" ||
          listing.rentToOwn?.creditCheckRequired) && (
          <QuestionCard>
            <QuestionLabel>
              <CreditCard size={18} />
              What's your estimated credit score?
            </QuestionLabel>
            <Form.Item
              name="creditScore"
              rules={[
                {
                  required: true,
                  message: "Please select your credit score range",
                },
              ]}
            >
              <StyledSelect
                size="large"
                placeholder="Select credit score range"
              >
                <Option value="excellent_720">Excellent (720+)</Option>
                <Option value="good_650">Good (650-719)</Option>
                <Option value="fair_600">Fair (600-649)</Option>
                <Option value="unknown">I don't know</Option>
              </StyledSelect>
            </Form.Item>
            {listing.rules?.minCreditScore && (
              <RequirementHint>
                This property requires: {listing.rules.minCreditScore}+
              </RequirementHint>
            )}
          </QuestionCard>
        )}

        <QuestionCard>
          <QuestionLabel>
            <DollarSign size={18} />
            What's your combined monthly income?
          </QuestionLabel>
          <Form.Item
            name="monthlyIncome"
            rules={[
              { required: true, message: "Please enter your monthly income" },
              {
                type: "number",
                min: 0,
                message: "Please enter a valid amount",
              },
            ]}
          >
            <StyledInputNumber
              size="large"
              prefix="$"
              placeholder="5,000"
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              style={{ width: "100%" }}
            />
          </Form.Item>
          {listing.rules?.minIncomeMultiplier &&
            (() => {
              let monthlyPayment;
              if (listing.listingType === "rent_to_own" && listing.rentToOwn) {
                monthlyPayment =
                  listing.rentToOwn.monthlyPayment || listing.rentAmount;
              } else {
                monthlyPayment = listing.rentAmount;
              }

              if (monthlyPayment) {
                const requiredIncome =
                  monthlyPayment * listing.rules.minIncomeMultiplier;
                const paymentType =
                  listing.listingType === "rent_to_own"
                    ? "monthly payment"
                    : "rent";
                return (
                  <RequirementHint>
                    This property requires: {formatRent(requiredIncome)}+ (
                    {listing.rules.minIncomeMultiplier}x {paymentType})
                  </RequirementHint>
                );
              }
              return null;
            })()}
        </QuestionCard>

        {/* Additional Screening Questions - Living Conditions */}
        {listing.rules?.askSmoking && (
          <QuestionCard>
            <QuestionLabel>Do you smoke?</QuestionLabel>
            <Form.Item
              name="smoking"
              rules={[{ required: true, message: "Please select an option" }]}
            >
              <ToggleButtons>
                <ToggleButton
                  type="button"
                  $active={form.getFieldValue("smoking") === false}
                  onClick={() => form.setFieldValue("smoking", false)}
                >
                  No
                </ToggleButton>
                <ToggleButton
                  type="button"
                  $active={form.getFieldValue("smoking") === true}
                  onClick={() => form.setFieldValue("smoking", true)}
                >
                  Yes
                </ToggleButton>
              </ToggleButtons>
            </Form.Item>
          </QuestionCard>
        )}

        {listing.rules?.askOccupants && (
          <QuestionCard>
            <QuestionLabel>
              Total number of occupants including you:
            </QuestionLabel>
            <Form.Item
              name="numberOfOccupants"
              rules={[
                {
                  required: true,
                  message: "Please enter the number of occupants",
                },
                {
                  type: "number",
                  min: 1,
                  message: "Please enter a valid number",
                },
              ]}
            >
              <StyledInputNumber
                size="large"
                placeholder="2"
                min={1}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </QuestionCard>
        )}

        {/* Additional Screening Questions - Financial */}
        {listing.rules?.askGuarantor && (
          <QuestionCard>
            <QuestionLabel>Do you have a guarantor?</QuestionLabel>
            <Form.Item
              name="hasGuarantor"
              rules={[{ required: true, message: "Please select an option" }]}
            >
              <ToggleButtons>
                <ToggleButton
                  type="button"
                  $active={form.getFieldValue("hasGuarantor") === false}
                  onClick={() => form.setFieldValue("hasGuarantor", false)}
                >
                  No
                </ToggleButton>
                <ToggleButton
                  type="button"
                  $active={form.getFieldValue("hasGuarantor") === true}
                  onClick={() => form.setFieldValue("hasGuarantor", true)}
                >
                  Yes
                </ToggleButton>
              </ToggleButtons>
            </Form.Item>
          </QuestionCard>
        )}

        {/* Additional Screening Questions - Legal */}
        {listing.rules?.askLawsuit && (
          <QuestionCard>
            <QuestionLabel>Have you been party to a lawsuit?</QuestionLabel>
            <Form.Item
              name="hasLawsuit"
              rules={[{ required: true, message: "Please select an option" }]}
            >
              <ToggleButtons>
                <ToggleButton
                  type="button"
                  $active={form.getFieldValue("hasLawsuit") === false}
                  onClick={() => form.setFieldValue("hasLawsuit", false)}
                >
                  No
                </ToggleButton>
                <ToggleButton
                  type="button"
                  $active={form.getFieldValue("hasLawsuit") === true}
                  onClick={() => form.setFieldValue("hasLawsuit", true)}
                >
                  Yes
                </ToggleButton>
              </ToggleButtons>
            </Form.Item>
          </QuestionCard>
        )}

        {listing.rules?.askFelony && (
          <QuestionCard>
            <QuestionLabel>Have you been convicted of a felony?</QuestionLabel>
            <Form.Item
              name="hasFelony"
              rules={[{ required: true, message: "Please select an option" }]}
            >
              <ToggleButtons>
                <ToggleButton
                  type="button"
                  $active={form.getFieldValue("hasFelony") === false}
                  onClick={() => form.setFieldValue("hasFelony", false)}
                >
                  No
                </ToggleButton>
                <ToggleButton
                  type="button"
                  $active={form.getFieldValue("hasFelony") === true}
                  onClick={() => form.setFieldValue("hasFelony", true)}
                >
                  Yes
                </ToggleButton>
              </ToggleButtons>
            </Form.Item>
          </QuestionCard>
        )}

        {/* Qualification Warning */}
        {showWarning && isQualified === false && (
          <WarningCard>
            <AlertCircle size={20} />
            <WarningText>
              <strong>Note:</strong> Based on the requirements for this
              property, your application may not meet all criteria. However, you
              can still submit your inquiry and the landlord will review your
              situation.
            </WarningText>
          </WarningCard>
        )}

        {/* Qualified Badge */}
        {isQualified === true && (
          <QualifiedCard>
            <CheckCircle size={20} />
            <QualifiedText>
              Great! You meet all the requirements for this property.
            </QualifiedText>
          </QualifiedCard>
        )}

        {/* Optional Message */}
        <SectionTitle>
          <MessageSquare size={18} />
          Additional Message (Optional)
        </SectionTitle>

        <Form.Item name="message">
          <StyledTextArea
            placeholder="Tell the landlord a bit about yourself, your move-in timeline, or any questions you have..."
            rows={4}
          />
        </Form.Item>

        {/* Submit Button */}
        <Form.Item>
          <SubmitButton
            type="primary"
            htmlType="submit"
            size="large"
            loading={submitInquiry.isLoading}
            block
          >
            {submitInquiry.isLoading ? "Sending..." : "Send Inquiry"}
          </SubmitButton>
        </Form.Item>

        <PrivacyNote>
          By submitting, you agree to be contacted by the property owner. Your
          information is kept private and secure.
        </PrivacyNote>
      </Form>
    </StyledModal>
  );
};

// Styled Components
const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
  }

  .ant-modal-body {
    padding: 0;
  }
`;

const ModalHeader = styled.div`
  padding: 2rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
`;

const ModalTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: white;
  margin: 0 0 0.5rem 0;
`;

const ModalSubtitle = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 1.5rem 0 1rem 0;
  padding: 0 2rem;

  svg {
    color: #3b82f6;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding: 0 2rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StyledInput = styled(Input)`
  .ant-input-prefix {
    color: #9ca3af;
  }
`;

const QuestionCard = styled.div`
  background: #f9fafb;
  padding: 1.5rem;
  margin: 0 2rem 1rem 2rem;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
  transition: border-color 0.2s;

  &:hover {
    border-color: #3b82f6;
  }
`;

const QuestionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;

  svg {
    color: #3b82f6;
  }
`;

const StyledDatePicker = styled(DatePicker)``;

const ToggleButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

const ToggleButton = styled.button`
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 500;
  background: ${(props) => (props.$active ? "#3b82f6" : "white")};
  color: ${(props) => (props.$active ? "white" : "#374151")};
  border: 2px solid ${(props) => (props.$active ? "#3b82f6" : "#d1d5db")};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #3b82f6;
    ${(props) => !props.$active && "background: #eff6ff;"}
  }
`;

const PetNote = styled.div`
  margin-top: 0.75rem;
  padding: 0.5rem;
  background: white;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #6b7280;
  text-align: center;
`;

const StyledSelect = styled(Select)``;

const StyledInputNumber = styled(InputNumber)``;

const RequirementHint = styled.div`
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
  font-style: italic;
`;

const WarningCard = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  margin: 0 2rem 1rem 2rem;
  background: #fef3c7;
  border: 2px solid #f59e0b;
  border-radius: 12px;

  svg {
    flex-shrink: 0;
    color: #f59e0b;
  }
`;

const WarningText = styled.div`
  font-size: 0.875rem;
  color: #92400e;
  line-height: 1.5;

  strong {
    font-weight: 600;
  }
`;

const QualifiedCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  margin: 0 2rem 1rem 2rem;
  background: #d1fae5;
  border: 2px solid #10b981;
  border-radius: 12px;

  svg {
    flex-shrink: 0;
    color: #10b981;
  }
`;

const QualifiedText = styled.div`
  font-size: 0.875rem;
  color: #065f46;
  font-weight: 500;
`;

const StyledTextArea = styled(TextArea)``;

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.875rem 2rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 6px rgba(59, 130, 246, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(59, 130, 246, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrivacyNote = styled.p`
  font-size: 0.75rem;
  color: #9ca3af;
  text-align: center;
  margin: 1rem 0 0 0;
  padding: 0 2rem 2rem 2rem;
  line-height: 1.5;
`;

export default InquiryModal;
